import React, { useMemo, useCallback, useState, useRef } from 'react';

import { useReactTable, getCoreRowModel } from '@tanstack/react-table';
import styled from 'styled-components';

import { useCurrentProject } from '@adsk/alloy-provider';
import A from '@adsk/alloy-react-a';
import Checkbox from '@adsk/alloy-react-checkbox';
import HighlightText from '@adsk/alloy-react-highlight-text';
import ProgressBar from '@adsk/alloy-react-progress-bar';
import StatusColorIndicator from '@adsk/alloy-react-status-color-indicator';
import {
  LoadMoreDataRow,
  useTable,
  TableHeaderGroup,
  TableHeader,
  TableRow,
  TableCell,
} from '@adsk/alloy-react-table';
import Theme from '@adsk/alloy-react-theme';
import Tooltip, { OverflowTooltip } from '@adsk/alloy-react-tooltip';

import { useCostContext } from './CostProvider';
import { useTranslation } from './i18n';
import {
  getStatus,
  STATUS_COLOR_MAPPING,
  shouldShowStatus,
  MAPPING_KEY,
} from '../helpers/consts';
import { getDeepLink } from '../helpers/formatEntities';
import { customizedType } from '../helpers/localeHelper';

const StyledScrollContainer = styled.div`
  height: 480px;
  overflow-y: auto;
  overflow-x: hidden;
`;

const StyledStickyThead = styled.thead`
  position: sticky;
  margin: 0;
  top: 0;
  z-index: 1;
`;

const DEFAULT_PROPS = {
  disabledIds: [],
  entities: [],
  selectedIds: [],
  filters: { searchExpression: '' },
};

const CustomDataView = ({
  disabledIds = DEFAULT_PROPS.disabledIds,
  entities = DEFAULT_PROPS.entities,
  selectedIds = DEFAULT_PROPS.selectedIds,
  filters: { searchExpression } = DEFAULT_PROPS.filters,
  onLoadMore,
  multiple,
  error,
  setSelection,
  onChange,
  onTryAgain,
  type = '',
}) => {
  const project = useCurrentProject();
  const { t } = useTranslation();
  const { terminologies } = useCostContext();
  const rowSelection = useMemo(() => {
    const rowSelection = {};
    for (const id of selectedIds) {
      rowSelection[id] = true;
    }
    return rowSelection;
  }, [selectedIds]);

  const [loading, setLoading] = useState(false);
  const dataTable = useRef(null);

  const columns = useMemo(
    () => [
      {
        id: 'select',
        size: 52,
        header: ({ table }) => (
          <Checkbox
            checked={
              table.getIsSomeRowsSelected()
                ? 'indeterminate'
                : table.getIsAllRowsSelected()
            }
            onChange={() => {
              // Toggle visible rows only — out-of-view selections (from other
              // searches/filters) are preserved by going through
              // onRowSelectionChange + onChange.
              table.toggleAllRowsSelected(!table.getIsAllRowsSelected());
            }}
          />
        ),
        cell: ({ row }) => (
          <Checkbox
            checked={
              row.getIsSomeSelected() ? 'indeterminate' : row.getIsSelected()
            }
            disabled={!row.getCanSelect() || disabledIds.includes(row.id)}
          />
        ),
      },
      {
        id: MAPPING_KEY[type] || t('CODE'),
        size: shouldShowStatus(type) ? 150 : 300,
        minWidth: 150,
        header: () => t(MAPPING_KEY[type]),
        cell: ({ row }) => (
          <A
            target="__blank"
            href={getDeepLink(row.original, project, type)}
            style={{ width: '100% !important', pointEvents: 'all !important' }}
          >
            <OverflowTooltip variant={Tooltip.VARIANTS.DARK}>
              <HighlightText
                style={{ color: Theme.colors.adskBlue500 }}
                matched={searchExpression}
                text={row.original[MAPPING_KEY[type]]}
              />
            </OverflowTooltip>
          </A>
        ),
      },
      {
        id: 'name',
        size: 300,
        minWidth: 200,
        header: () => t('NAME'),
        cell: ({ row }) => (
          <OverflowTooltip variant={Tooltip.VARIANTS.DARK}>
            <HighlightText
              matched={searchExpression}
              text={row.original.name}
            />
          </OverflowTooltip>
        ),
      },
      ...(shouldShowStatus(type)
        ? [
            {
              id: 'status',
              accessor: 'status',
              size: 200,
              minWidth: 150,
              header: () => t('STATUS'),
              cell: ({ row }) => {
                const status = getStatus(type, row.original);
                return (
                  <OverflowTooltip>
                    <div style={{ display: 'flex', alignItems: 'center' }}>
                      <StatusColorIndicator
                        statusColor={STATUS_COLOR_MAPPING[status]}
                        style={{
                          height: '16px',
                          width: '4px',
                          cursor: 'default',
                        }}
                      />
                      <span style={{ marginLeft: '5px' }}>
                        <HighlightText
                          matched={searchExpression}
                          text={
                            status
                              ? customizedType(t, status, terminologies)
                              : null
                          }
                        />
                      </span>
                    </div>
                  </OverflowTooltip>
                );
              },
            },
          ]
        : []),
    ],
    [t, disabledIds, project, searchExpression, terminologies, type],
  );

  const table = useReactTable({
    data: entities,
    getRowId: (row) => row.id,
    columns,
    enableRowSelection: (row) => !disabledIds.includes[row.id],
    enableMultiRowSelection: multiple,
    enableSorting: false,
    getCoreRowModel: getCoreRowModel(),
    state: {
      rowSelection,
    },
    // Translate visible-row toggles into per-item onChange calls so global
    // selections from other searches/filters are preserved. setSelection is
    // only used to clear when no items remain selected anywhere.
    onRowSelectionChange: (updaterOrValue) => {
      const curSelection =
        typeof updaterOrValue === 'function'
          ? updaterOrValue(rowSelection)
          : updaterOrValue;

      const preKeys = Object.keys(rowSelection);
      const curKeys = Object.keys(curSelection);
      const findEntity = (id) => entities.find((entity) => entity.id === id);
      const added = curKeys
        .filter((id) => !preKeys.includes(id))
        .map(findEntity)
        .filter(Boolean);
      const removed = preKeys
        .filter((id) => !curKeys.includes(id))
        .map(findEntity)
        .filter(Boolean);

      if (curKeys.length === 0) {
        // Deselecting all rows — use setSelection to clear all selections globally.
        setSelection([]);
      } else {
        removed.forEach((entity) => onChange({ entity, checked: false }));
        added.forEach((entity) => onChange({ entity, checked: true }));
      }
    },
  });

  const {
    getTableHeaderProps,
    getTableHeaderGroupProps,
    getTableRowProps,
    getTableCellProps,
  } = useTable({ table, enableMultiRowRangeSelection: true });

  const onScroll = useCallback(async () => {
    if (dataTable.current) {
      const tableElement = dataTable.current;
      // avoid loading data when has error or already in loading
      if (
        !loading &&
        !error &&
        onLoadMore &&
        Math.round(tableElement.scrollTop) ===
          Math.round(tableElement?.scrollHeight - tableElement?.offsetHeight)
      ) {
        setLoading(true);
        await onLoadMore();
        setLoading(false);
      }
    }
  }, [onLoadMore, error, loading, dataTable]);

  return (
    <StyledScrollContainer onScroll={onScroll} ref={dataTable}>
      <table
        style={{
          borderSpacing: 0,
          width: '100%',
        }}
      >
        <StyledStickyThead>
          {table.getHeaderGroups().map((headerGroup) => (
            <TableHeaderGroup
              key={headerGroup.id}
              {...getTableHeaderGroupProps(headerGroup)}
            >
              {headerGroup.headers.map((header) => (
                <TableHeader key={header.id} {...getTableHeaderProps(header)} />
              ))}
            </TableHeaderGroup>
          ))}
        </StyledStickyThead>
        <tbody>
          {table.getRowModel().rows.map((row) => (
            <TableRow
              key={row.id}
              id={row.id}
              disabled={disabledIds.includes(row.id)}
              {...getTableRowProps(row)}
            >
              {row.getVisibleCells().map((cell) => (
                <TableCell key={cell.id} {...getTableCellProps(cell)} />
              ))}
            </TableRow>
          ))}
          {!!error && onLoadMore && (
            <LoadMoreDataRow
              hasError={!!error}
              onRetry={async () => onTryAgain()}
              onLoad={
                onLoadMore
                  ? async () => onLoadMore()
                  : error
                    ? async () => onTryAgain()
                    : undefined
              }
            />
          )}
        </tbody>
      </table>
      {/* marginTop is needed to avoid to trigger onScroll when reaching bottom */}
      {loading && !error && <ProgressBar style={{ marginTop: '-4px' }} />}
    </StyledScrollContainer>
  );
};

CustomDataView.displayName = 'CustomDataView';

export default CustomDataView;
